import { displayAddress } from '@/webapp/siteAddress';
import { SITE_STATUS_LABEL } from '@/utils/websiteSheet';
import { Chip } from '../../ui';
import type { SiteCheck } from '../../state/siteStatus';

/**
 * The website's address and whether it is up, under the app's name and above the preview.
 *
 * The address used to be in the Publish panel only, below a preview frame 844px tall — so on a
 * laptop the thing the owner had just made was below the fold, and a build that landed here
 * showed the app with no hint of where it lived. This line is the first thing under the name.
 *
 * The status word is the server's (see state/siteStatus.ts) and appears only once it has
 * answered. A page the server says is down is not a link: it would open "This site isn't
 * available", which is what Offline already says.
 */
export default function SiteLine({ webUrl, site }: { webUrl?: string; site: SiteCheck }) {
  const known = site.kind === 'known' ? site : null;
  const offline = known?.status === 'offline';
  // The server's address for a page it serves; the record's while it has not answered.
  const url = offline ? webUrl : (known?.siteUrl ?? webUrl);
  if (!url && !known) return null;
  const address = url ? displayAddress(url) : '';

  return (
    <p className="editor-site type-body">
      {url && !offline ? (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open your website ${address} in a new tab`}
        >
          {address} <span aria-hidden="true">↗</span>
        </a>
      ) : (
        <span className="editor-site-address">{address || 'Your website'}</span>
      )}
      {known && (
        <Chip
          label={SITE_STATUS_LABEL[known.status]}
          tone={known.status === 'live' ? 'success' : known.status === 'password' ? 'primary' : 'neutral'}
        />
      )}
    </p>
  );
}
